import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
} from '@mui/material';
import {
  CalendarToday,
  Computer,
  Place,
  Groups,
  Check,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useReducedMotionPreferred } from '../../../hooks/useReducedMotionPreferred';

const MotionCard = motion(Card);

/**
 * PlanCard — mirrors LibraryDetailPage plan card visual style.
 *
 * @param {{ id, name, course_type, description, price_cents, duration_months, mode, batch_size, features: string[] }} plan
 * @param {boolean} featured
 * @param {(plan) => void} onShowInterest
 */
function PlanCard({ plan, featured, onShowInterest }) {
  const reducedMotion = useReducedMotionPreferred();

  const priceDisplay =
    plan.price_cents > 0
      ? `₹${plan.price_cents.toLocaleString()}`
      : 'Contact for pricing';

  const durationDisplay = plan.duration_months
    ? `for ${plan.duration_months} month${plan.duration_months > 1 ? 's' : ''}`
    : 'Flexible duration';

  return (
    <MotionCard
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      sx={{
        minWidth: { xs: 280, sm: 320 },
        maxWidth: 320,
        borderRadius: '16px',
        border: featured ? 2 : 1,
        borderColor: featured ? 'primary.main' : 'divider',
        position: 'relative',
        flexShrink: 0,
        cursor: 'default',
        bgcolor: featured ? 'primary.soft' : 'background.paper',
        transition: reducedMotion ? 'none' : 'all 0.3s ease',
        '&:hover': reducedMotion
          ? {}
          : { boxShadow: 8, transform: 'translateY(-8px)' },
      }}
    >
      {featured && (
        <Chip
          label="MOST POPULAR"
          size="small"
          color="primary"
          sx={{
            position: 'absolute',
            top: -12,
            right: 16,
            fontWeight: 700,
            fontSize: '0.7rem',
            boxShadow: 2,
          }}
        />
      )}

      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box mb={2}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            {plan.name}
          </Typography>
          {plan.course_type && (
            <Typography variant="caption" color="text.secondary">
              {plan.course_type}
            </Typography>
          )}
          {plan.description && (
            <Typography variant="body2" color="text.secondary" lineHeight={1.5} mt={0.5}>
              {plan.description}
            </Typography>
          )}
        </Box>

        {/* Price */}
        <Box mb={2}>
          <Typography
            variant="h3"
            fontWeight={800}
            color="primary.main"
            lineHeight={1}
          >
            {priceDisplay}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {durationDisplay}
          </Typography>
        </Box>

        {/* Feature rows */}
        <Box display="flex" flexDirection="column" gap={1} mb={3}>
          {plan.duration_months && (
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={500}>
                {plan.duration_months} month{plan.duration_months > 1 ? 's' : ''} duration
              </Typography>
            </Box>
          )}
          {plan.mode && (
            <Box display="flex" alignItems="center" gap={1}>
              {plan.mode === 'online' ? (
                <Computer sx={{ fontSize: 18, color: 'text.secondary' }} />
              ) : (
                <Place sx={{ fontSize: 18, color: 'text.secondary' }} />
              )}
              <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
                {plan.mode} classes
              </Typography>
            </Box>
          )}
          {plan.batch_size && (
            <Box display="flex" alignItems="center" gap={1}>
              <Groups sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={500}>
                Up to {plan.batch_size} students
              </Typography>
            </Box>
          )}
          {plan.features?.slice(0, 4).map((feature, idx) => (
            <Box key={idx} display="flex" alignItems="center" gap={1}>
              <Check sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography variant="body2" fontWeight={500}>
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={() => onShowInterest(plan)}
          sx={{
            borderRadius: '12px',
            py: 1.5,
            mt: 'auto',
            fontWeight: 700,
            fontSize: '0.95rem',
            boxShadow: 3,
          }}
        >
          Show Interest
        </Button>
      </CardContent>
    </MotionCard>
  );
}

export default PlanCard;
